import { LDate } from '@app/components/date/LDate';
import { LTime } from '@app/components/date/LTime';
import {
  LabelWrapper,
  SwitchWrapper,
} from '@app/modules/logger/screens/stream/components/audit/transcriptions/styles';
import TrascriptionsLabel from '@app/modules/logger/screens/stream/components/audit/transcriptions/TranscriptionsLabel';
import { ElasticSearchFormattedType, ElasticSearchReport } from '@app/modules/search/types';
import { toDateTimeStr, toTimestamp } from '@mi-front/core';
import { BtnLink, MrtTable, SwitchInput } from '@mi-front/ui';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import moment from 'moment';
import { formatTranscript, parseTranscriptionSearch } from './utils';

import { isVideoStation } from '@app/core/stations/helpers';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ReportResultProps } from '../../../../../../core/reports/types';
import { useActiveStation } from '../../../../../../core/stations/hooks/useActiveStation';
import { TsThumbnail } from '../../../../../../core/thumbnails/components/TsThumbnail';

import { FormattedMessage } from 'react-intl';
export function SearchResult({
  values,
  result,
  isLoading,
}: ReportResultProps<ElasticSearchReport>) {
  const { $t } = useIntl();
  const station = useActiveStation();
  const isVideo = isVideoStation(station);
  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>();

  const data = useMemo(() => {
    const filterWords = values.input.toLowerCase().split(/[+| ]/);

    return formatTranscript(parseTranscriptionSearch(result), filterWords);
  }, [values.input, result]);

  const [confidence, setConfidence] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    'text-with-confidence': confidence,
    text: !confidence,
    'transcription-thumbnail': isVideo,
  });

  useEffect(() => {
    setColumnVisibility({
      'text-with-confidence': confidence,
      text: !confidence,
      'transcription-thumbnail': isVideo,
    });
  }, [confidence, station]);

  return (
    <>
      <LabelWrapper>
        {confidence ? <TrascriptionsLabel /> : <div />}
        <SwitchWrapper>
          <SwitchInput
            size={'sm'}
            checked={confidence}
            onChange={setConfidence}
            label={$t({
              id: 'audit.transcriptionScreen.confidence',
              defaultMessage: 'Eficiencia',
            })}
          />
        </SwitchWrapper>
      </LabelWrapper>
      <MrtTable<ElasticSearchFormattedType>
        columns={[
          {
            id: 'time',
            header: $t({
              id: 'search.dateTime',
              defaultMessage: 'Fecha y hora',
            }),
            accessorFn: (row) => row.time,
            Cell: ({ row: { original } }) => (
              <div style={{ display: 'flex', gap: '0.375rem' }}>
                <LDate
                  value={toDateTimeStr(original.time)}
                  day="2-digit"
                  month="2-digit"
                  year="2-digit"
                />
                <LTime value={toDateTimeStr(original.time)} second={'numeric'} />
              </div>
            ),
            enableSorting: true,
            size: 60,
          },
          {
            id: 'transcription-thumbnail',
            enableSorting: false,
            header: $t({
              id: 'search.preview',
              defaultMessage: 'Previsualización',
            }),
            Cell: ({ row: { original, index } }) => (
              <TsThumbnail
                stationId={station.id}
                ts={toTimestamp(original.time)}
                animated={index === hoveredIndex}
              />
            ),
            size: 100,
          },
          {
            id: 'text-with-confidence',
            enableSorting: false,
            accessorFn: (row) => row.wp_words,
            header: $t({
              id: 'search.transcription',
              defaultMessage: 'Transcripción',
            }),
            Cell: ({ renderedCellValue }) => {
              return <div>{renderedCellValue}</div>;
            },
            size: 250,
          },
          {
            id: 'text',
            enableSorting: false,
            accessorFn: (row) => row.raw_content,
            header: $t({
              id: 'search.transcription',
              defaultMessage: 'Transcripción',
            }),
            Cell: ({ renderedCellValue }) => {
              return <div>{renderedCellValue}</div>;
            },
            size: 250,
          },
          {
            id: 'actions',
            enableSorting: false,
            accessorFn: () => '',
            header: $t({
              id: 'common.text.actions',
              defaultMessage: 'Acciones',
            }),
            size: 60,
            Cell: ({ row: { original } }) => {
              const dateTime = moment(original.time).toISOString();
              return (
                <BtnLink
                  size="xs"
                  to={`/logger/s/${
                    station.slug
                  }?tab=transcriptions&cursor=${dateTime}&length=${Math.abs(
                    original.length,
                  )}&transcriptionTime=${dateTime}`}
                  openInNewTab
                  icon={<OpenInNewIcon />}
                  className="mr-2"
                >
                  <FormattedMessage
                    id="audit.matchesScreen.showOnStreamBtn"
                    defaultMessage="Mostrar en stream"
                  />
                </BtnLink>
              );
            },
          },
        ]}
        muiTableBodyRowProps={({ row }) => {
          return {
            onMouseEnter: () => setHoveredIndex(row.index),
            onMouseLeave: () => setHoveredIndex(undefined),
          };
        }}
        data={data}
        enableExpandAll={false}
        enableGlobalFilter={false}
        enableFilters={false}
        memoizeColumns={false}
        enableHiding={false}
        enableColumnActions={false}
        state={{
          isLoading,
          columnVisibility,
        }}
      />
    </>
  );
}
